import React, { useState } from "react";
import {
  Box,
  Typography,
  useTheme,
  Divider,
  Fade,
} from "@mui/material";
import {
  MoneyOff as ExpenseIcon,
  AttachMoney as IncomeIcon,
} from "@mui/icons-material";
import { CategoryType } from "../../models/finance/CategoryType";
import Category from "../../models/finance/Category";
import CategoryEditDialogComponent from "./CategoryEditDialogComponent";
import CategoryItem from "./CategoryItem";
import AddCategoryFormComponent from "./AddCategoryFormComponent";
import { ModernCard } from "../common/StyledComponents";

interface CategoryListProps {
  categoryType: CategoryType;
  categories: Category[];
  onAddCategory: (title: string, type: CategoryType) => void;
  onDeleteCategory: (category: Category, fallbackId?: string) => void;
  onUpdateCategory: (
    oldCategory: Category,
    newTitle?: string,
    newIsActive?: boolean
  ) => void;
  getFinanceItemsNumber: (
    categoryType: CategoryType,
    categoryId: string
  ) => Promise<number>;
  addError?: string | null;
  onClearAddError?: () => void;
}

const CategoryListComponent: React.FC<CategoryListProps> = ({
  categoryType,
  categories,
  onAddCategory,
  onDeleteCategory,
  onUpdateCategory,
  getFinanceItemsNumber,
  addError,
  onClearAddError,
}) => {
  const theme = useTheme();
  const [showInactive, setShowInactive] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(
    null
  );

  const handleOpenDialog = (category: Category) => {
    setSelectedCategory({ ...category });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedCategory(null);
  };

  const activeCategories = categories
    .filter((cat) => cat.isActive)
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

  const inactiveCategories = categories
    .filter((cat) => !cat.isActive)
    .sort((a, b) => {
      const dateA = new Date(a.deactivatedAt || 0).getTime();
      const dateB = new Date(b.deactivatedAt || 0).getTime();
      return dateB - dateA;
    });

  const filteredCategories = showInactive
    ? [...activeCategories, ...inactiveCategories]
    : activeCategories;

  const isExpense = categoryType === CategoryType.Expense;
  const themeColor = isExpense ? theme.palette.error : theme.palette.success;
  const icon = isExpense ? <ExpenseIcon /> : <IncomeIcon />;

  return (
    <Fade in={true} timeout={800}>
      <ModernCard sx={{ height: 'fit-content' }}>
        {/* Header */}
        <Box
          sx={{
            p: 3,
            borderBottom: `1px solid ${theme.palette.divider}`,
            background: `linear-gradient(135deg, ${themeColor.light}10 0%, ${themeColor.main}10 100%)`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
                borderRadius: 2,
                background: `linear-gradient(135deg, ${themeColor.main}, ${themeColor.dark})`,
                color: 'white',
              }}
            >
              {icon}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, color: themeColor.main }}>
                {isExpense ? "Expense" : "Income"} Categories
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {activeCategories.length} active • {inactiveCategories.length} inactive
              </Typography>
            </Box>
          </Box>

          {/* Add Category Form */}
          <AddCategoryFormComponent
            onAdd={(name) => onAddCategory(name, categoryType)}
            showInactive={showInactive}
            onToggleShowInactive={() => setShowInactive(!showInactive)}
            error={addError}
            onClearError={onClearAddError}
          />
        </Box>

        {/* Categories Content */}
        <Box sx={{ p: 3 }}>
          {filteredCategories.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  background: theme.palette.grey[100],
                  color: theme.palette.grey[400],
                  mb: 2,
                }}
              >
                {icon}
              </Box>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No {isExpense ? "expense" : "income"} categories
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {showInactive
                  ? "No categories found. Create your first category above."
                  : "No active categories. Create one above or toggle to show inactive categories."
                }
              </Typography>
            </Box>
          ) : (
            <>
              {/* Active Categories */}
              {activeCategories.length > 0 && (
                <Box sx={{ mb: activeCategories.length > 0 && inactiveCategories.length > 0 && showInactive ? 3 : 0 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
                    Active Categories ({activeCategories.length})
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                    {activeCategories.map((cat) => (
                      <CategoryItem
                        key={cat.id}
                        category={cat}
                        onClick={() => handleOpenDialog(cat)}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Inactive Categories (if showing) */}
              {showInactive && inactiveCategories.length > 0 && (
                <>
                  {activeCategories.length > 0 && <Divider sx={{ my: 3 }} />}
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2, fontWeight: 600 }}>
                      Inactive Categories ({inactiveCategories.length})
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                      {inactiveCategories.map((cat) => (
                        <CategoryItem
                          key={cat.id}
                          category={cat}
                          onClick={() => handleOpenDialog(cat)}
                        />
                      ))}
                    </Box>
                  </Box>
                </>
              )}
            </>
          )}
        </Box>

        {/* Edit Dialog */}
        {selectedCategory && (
          <CategoryEditDialogComponent
            open={dialogOpen}
            category={selectedCategory}
            categories={categories}
            categoryType={categoryType}
            onClose={handleCloseDialog}
            onDelete={onDeleteCategory}
            onUpdate={onUpdateCategory}
            getFinanceItemsNumber={getFinanceItemsNumber}
          />
        )}
      </ModernCard>
    </Fade>
  );
};

export default CategoryListComponent;
